import React, { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { API_ROOT } from "@/config";
import LoadingSpinner from "@/components/ui/loading-spinner";

interface BrandProtectedRouteProps {
  children: React.ReactNode;
  requireSubscription?: boolean; // Whether to require active subscription
}

const BrandProtectedRoute = ({ children, requireSubscription = true }: BrandProtectedRouteProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const token = localStorage.getItem("token");
  const location = useLocation();

  useEffect(() => {
    const verifyAccess = async () => {
      if (!token) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        // First verify the token
        const userResponse = await fetch(`${API_ROOT}/users/me`, {
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });

        if (!userResponse.ok) {
          // Token is invalid or expired
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("userType");
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        setIsAuthenticated(true);

        // If subscription check is required, verify subscription status
        if (requireSubscription) {
          const subResponse = await fetch(`${API_ROOT}/subscription/status`, {
            headers: {
              "Authorization": `Bearer ${token}`
            }
          });

          if (subResponse.ok) {
            const subData = await subResponse.json();
            const isActive = subData.has_subscription && 
              (subData.status === 'active' || subData.status === 'trialing');
            setHasActiveSubscription(isActive);
          } else {
            setHasActiveSubscription(false);
          }
        } else {
          // Not requiring subscription for this route
          setHasActiveSubscription(true);
        }
      } catch (error) {
        console.error("Error verifying access:", error);
        setIsAuthenticated(false);
        setHasActiveSubscription(false);
      } finally {
        setIsLoading(false);
      }
    };

    verifyAccess();
  }, [token, requireSubscription]);

  // Show loading state
  if (isLoading) {
    return <LoadingSpinner size="lg" text="Loading..." fullPage />;
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Authenticated but no subscription - redirect to subscription page
  // (unless already on subscription pages)
  if (requireSubscription && !hasActiveSubscription) {
    return <Navigate to="/subscription" replace state={{ from: location }} />;
  }

  return <>{children}</>;
};

export default BrandProtectedRoute;
